import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { IErrorModel, IPaginatedResponse } from '@core/models/generic.model';
import mapService from 'services/api/map.service';

// -------------------------------------------------------------------------
interface ICustomerMapData {
    id: number;
    name: string;
    latitude: number;
    longitude: number;
}
interface IMapSliceState {
    loading: boolean;
    error: IErrorModel | undefined;
    map: ICustomerMapData[]
}

const initialState: IMapSliceState = {
    loading: false,
    error: {} as IErrorModel,
    map: [],
};

// -------------------------------------------------------------------------

export const getMapThunk = createAsyncThunk<
    IPaginatedResponse,
    void,
    { rejectValue: IErrorModel }
>(`/map/getMap`, async (_, { rejectWithValue }: any) => {
    try {
        const response: any = await mapService.getMap();
        return response.data
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to fetch map data', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const importCustomersMapThunk = createAsyncThunk<
    void,
    FormData,
    { rejectValue: IErrorModel }
>(`/map/importCustomersMap`, async (body, { rejectWithValue }: any) => {
    try {
        const response: any = await mapService.importCustomersMap(body);
        return response.data.map;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to import customers map', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const addDescriptionMapThunk = createAsyncThunk<
    void,
    { id: number; body: { description: string } },
    { rejectValue: IErrorModel }
>(`/map/addDescriptionMap`, async ({ id, body }, { rejectWithValue }: any) => {
    try {
        const response: any = await mapService.addDescriptionMap(id, body);
        return response.data.map;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to add description map', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

// -------------------------------------------------------------------------


const mapSlice = createSlice({
    name: `map`,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Get Map
        builder.addCase(getMapThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getMapThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.map = action.payload;
        });
        builder.addCase(getMapThunk.rejected, (state, action) => {
            state.loading = false;
            state.map = [];
            state.error = action.payload;
        });
        // Import Customers Map
        builder.addCase(importCustomersMapThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(importCustomersMapThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.map = action.payload;
        });
        builder.addCase(importCustomersMapThunk.rejected, (state, action) => {
            state.loading = false;
            state.map = [];
            state.error = action.payload;
        });
        // Add Description Map
        builder.addCase(addDescriptionMapThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(addDescriptionMapThunk.fulfilled, (state: any, action: any) => {
            state.loading = false;
            state.map = action.payload;
        });
        builder.addCase(addDescriptionMapThunk.rejected, (state, action) => {
            state.loading = false;
            state.map = [];
            state.error = action.payload;
        });
    }
});

// -------------------------------------------------------------------------

export default mapSlice;
export const mapReducer = mapSlice.reducer;

